/**
 * Manager Focus Dashboard — "Store Owner" View v2
 *
 * Single-viewport, no-scroll design. Two tabs: Performance | Recent Entries.
 * Dark hero with ring + sales input all on one screen.
 * Branch switcher (multi-branch) as compact pills.
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { User } from '../../domain/entities';
import { cn } from '../../ui/utils';
import { useToast } from '../../ui/Toast';
import { isRemoteMode } from '../../config/repository';
import { apiFetch } from '../../data/ApiModuleRepository';
import {
  DollarSign, Target, Building2, Trophy, Zap, Calendar,
  TrendingUp, AlertTriangle, RefreshCw, Loader2,
  Clock, History, BarChart3, CheckCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

interface MapLocation {
  id: string;
  org_id: string;
  brand_id: string;
  branch_id: string;
  name: string;
  type: 'INTERNAL' | 'COMPETITOR';
  lat: number;
  lon: number;
  daily_target: number;
  monthly_target: number;
  current_sales?: number;
  brand_name?: string;
  brand_logo_url?: string;
  branch_name?: string;
  branch_code?: string;
  effective_daily_target?: number;
  effective_monthly_target?: number;
}

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

function getEffectiveTarget(loc: MapLocation): number {
  return loc.effective_daily_target ?? loc.daily_target ?? 0;
}

function getSalesRatio(loc: MapLocation): number {
  const t = getEffectiveTarget(loc);
  return t > 0 ? (loc.current_sales ?? 0) / t : 0;
}

function getPerformanceColor(ratio: number): string {
  if (ratio >= 0.8) return '#10b981';
  if (ratio >= 0.4) return '#f59e0b';
  return '#ef4444';
}

function formatCurrency(n: number): string {
  return n.toLocaleString('en-QA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function getMediaUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '';
  const token = localStorage.getItem('dm_jwt');
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${API_BASE}${url}${q}`;
}

interface BranchMetrics {
  loc: MapLocation;
  todayRatio: number;
  todayColor: string;
  dailyTarget: number;
  mTotal: number;
  mTarget: number;
  mRemaining: number;
  mRatio: number;
  daysLeft: number;
  dailyRequired: number;
  runRate: number;
  runRateGap: number;
  isCelebration: boolean;
  logoUrl: string | undefined;
}

function computeMetrics(
  loc: MapLocation,
  monthlyTotals: Record<string, { total: number; count: number }>,
  dayOfMonth: number,
  daysInMonth: number,
): BranchMetrics {
  const todayRatio = getSalesRatio(loc);
  const todayColor = getPerformanceColor(todayRatio);
  const dailyTarget = getEffectiveTarget(loc);
  const mTotal = monthlyTotals[loc.id]?.total ?? 0;
  const mTarget = loc.effective_monthly_target || loc.monthly_target || 0;
  const mRemaining = Math.max(0, mTarget - mTotal);
  const mRatio = mTarget > 0 ? mTotal / mTarget : 0;
  const daysLeft = Math.max(0, daysInMonth - dayOfMonth);
  const dailyRequired = daysLeft > 0 ? Math.round(mRemaining / daysLeft) : 0;
  const runRate = dayOfMonth > 0 ? Math.round((mTotal / dayOfMonth) * daysInMonth) : 0;
  const runRateGap = runRate - mTarget;
  const isCelebration = mTarget > 0 && mRatio >= 1;
  const logoUrl = getMediaUrl(loc.brand_logo_url);
  return { loc, todayRatio, todayColor, dailyTarget, mTotal, mTarget, mRemaining, mRatio, daysLeft, dailyRequired, runRate, runRateGap, isCelebration, logoUrl };
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

export function ManagerFocusDashboard({ user }: { user: User | null }) {
  const toast = useToast();
  const [locations, setLocations] = useState<MapLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [salesInputs, setSalesInputs] = useState<Record<string, string>>({});
  const [savingSales, setSavingSales] = useState<string | null>(null);
  const [monthlyTotals, setMonthlyTotals] = useState<Record<string, { total: number; count: number }>>({});
  const [recentEntries, setRecentEntries] = useState<any[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'history'>('dashboard');
  const [selectedDate, setSelectedDate] = useState<string>(''); // '' = today

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;  // YYYY-MM-DD
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const dayOfMonth = now.getDate();
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const loadData = useCallback(async () => {
    if (!isRemoteMode()) { setLoading(false); return; }
    setError(false);
    try {
      const [sumRes, monthRes] = await Promise.all([
        apiFetch('/api/map/sales/summary'),
        apiFetch(`/api/map/sales/monthly-summary?month=${currentMonth}`),
      ]);
      setLocations((sumRes as any).data ?? []);
      const md = (monthRes as any).data ?? {};
      const tMap: Record<string, { total: number; count: number }> = {};
      for (const t of (md.totals ?? [])) {
        tMap[t.location_id] = { total: Number(t.total_sales ?? 0), count: Number(t.entry_count ?? 0) };
      }
      setMonthlyTotals(tMap);
      setRecentEntries(md.recent ?? []);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [currentMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSalesSubmit = async (locId: string) => {
    const val = parseFloat(salesInputs[locId] || '0');
    if (!val || val <= 0) { toast.warning('Invalid', 'Enter a valid sales amount.'); return; }
    setSavingSales(locId);
    const entryDate = selectedDate || todayStr;
    try {
      await apiFetch('/api/map/sales', { method: 'POST', body: JSON.stringify({ locationId: locId, salesAmount: val, entryDate }) });
      const isBackfill = entryDate !== todayStr;
      toast.success(isBackfill ? 'Backfill Saved' : 'Sales Saved', isBackfill ? `Entry for ${entryDate} saved.` : 'Dashboard updated.');
      setSalesInputs(prev => ({ ...prev, [locId]: '' }));
      if (isBackfill) setSelectedDate('');
      loadData();
    } catch (e: any) { toast.error('Failed', e.message || 'Save failed.'); }
    finally { setSavingSales(null); }
  };

  const internal = locations.filter(l => l.type === 'INTERNAL');

  // ─── Loading / Error / Empty ───
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3">
        <Loader2 size={32} className="animate-spin text-indigo-400" />
        <p className="text-sm text-slate-400 font-medium">Loading your dashboard…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3">
        <AlertTriangle size={32} className="text-amber-400" />
        <p className="text-sm text-slate-500 font-medium">Failed to load data.</p>
        <button onClick={() => { setLoading(true); loadData(); }}
          className="flex items-center gap-1.5 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-colors">
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }
  if (internal.length === 0) {
    return (
      <div className="max-w-md mx-auto bg-white border border-slate-200 rounded-2xl p-12 text-center mt-12">
        <Building2 size={36} className="mx-auto text-slate-300 mb-3" />
        <p className="text-sm font-semibold text-slate-500">No branches assigned to you yet.</p>
        <p className="text-xs text-slate-400 mt-1">Contact your administrator to get branch access.</p>
      </div>
    );
  }

  // ─── Active branch metrics ───
  const activeLoc = internal[activeIdx] ?? internal[0];
  const m = computeMetrics(activeLoc, monthlyTotals, dayOfMonth, daysInMonth);
  const { loc, todayRatio, todayColor, dailyTarget, mTotal, mTarget, mRemaining, mRatio, daysLeft, dailyRequired, runRate, runRateGap, isCelebration, logoUrl } = m;
  const circumference = 2 * Math.PI * 52;
  const strokeDash = circumference * Math.min(mRatio, 1);
  const ringColor = isCelebration ? '#d4a017' : mRatio >= 0.8 ? '#10b981' : mRatio >= 0.5 ? '#f59e0b' : '#ef4444';

  const TABS = [
    { key: 'dashboard' as const, label: 'Dashboard', icon: <BarChart3 size={14} /> },
    { key: 'history' as const, label: 'Recent Entries', icon: <History size={14} />, badge: recentEntries.length },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      {/* ─── Top bar: Branch pills + Tabs ─── */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        {/* Branch selector pills */}
        {internal.length > 1 ? (
          <div className="flex gap-1.5 overflow-x-auto">
            {internal.map((b, idx) => {
              const isActive = idx === activeIdx;
              const bLogo = getMediaUrl(b.brand_logo_url);
              return (
                <button key={b.id} onClick={() => setActiveIdx(idx)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 border',
                    isActive
                      ? 'bg-slate-900 text-white border-slate-700 shadow-md'
                      : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                  )}>
                  {bLogo ? (
                    <img src={bLogo} alt="" className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <Building2 size={12} />
                  )}
                  {b.name}
                </button>
              );
            })}
          </div>
        ) : (
          <div />
        )}

        {/* Tab switcher */}
        <div className="flex bg-slate-100 rounded-xl p-1 gap-0.5">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all',
                activeTab === t.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              )}>
              {t.icon} {t.label}
              {t.badge != null && t.badge > 0 && (
                <span className="bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full text-[10px] font-bold ml-0.5">{t.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ─── Tab Content ─── */}
      <AnimatePresence mode="wait">
        {activeTab === 'dashboard' ? (
          <motion.div key={`dash-${activeLoc.id}`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}>

            {/* ─── Single dark card — EVERYTHING in one viewport ─── */}
            <div className={cn(
              'rounded-2xl overflow-hidden shadow-2xl',
              isCelebration
                ? 'bg-gradient-to-br from-amber-900 via-amber-800 to-emerald-900'
                : 'bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900'
            )}>
              {/* Accent stripe */}
              <div className="h-1" style={{ background: isCelebration
                ? 'linear-gradient(90deg, #d4a017, #fbbf24, #10b981)'
                : `linear-gradient(90deg, ${ringColor}80, ${ringColor}, ${ringColor}80)` }} />

              <div className="p-5 md:p-6">
                {/* ─── Header row: brand/branch + days left ─── */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className="w-10 h-10 rounded-xl object-cover border-2 shadow"
                        style={{ borderColor: ringColor }} />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
                        <Building2 size={18} className="text-white/60" />
                      </div>
                    )}
                    <div>
                      <h1 className="text-base font-black text-white leading-tight">{loc.name}</h1>
                      <p className="text-[11px] text-white/40">{loc.brand_name} · {dateStr}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-white/10 text-amber-300 border border-white/10">
                    ⏳ {daysLeft}d left
                  </div>
                </div>

                {/* ─── Main layout: Ring left + content right ─── */}
                <div className="flex gap-5 items-start">
                  {/* Ring — compact */}
                  <div className="relative w-32 h-32 shrink-0 self-center">
                    {isCelebration && (
                      <motion.div className="absolute inset-[-8px] rounded-full"
                        animate={{ opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 2.5, repeat: Infinity }}
                        style={{ background: 'radial-gradient(circle, rgba(212,160,23,0.3) 0%, transparent 70%)' }} />
                    )}
                    <svg viewBox="0 0 128 128" className="w-full h-full -rotate-90">
                      <circle cx="64" cy="64" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" />
                      <motion.circle cx="64" cy="64" r="52" fill="none"
                        stroke={ringColor} strokeWidth="9" strokeLinecap="round"
                        strokeDasharray={circumference}
                        initial={{ strokeDashoffset: circumference }}
                        animate={{ strokeDashoffset: circumference - strokeDash }}
                        transition={{ duration: 1.8, ease: 'easeOut' }} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      {isCelebration ? (
                        <>
                          <Trophy size={18} className="text-amber-400 mb-0.5" />
                          <p className="text-xl font-black text-amber-300">{(mRatio * 100).toFixed(0)}%</p>
                          <p className="text-[8px] text-emerald-400 font-bold uppercase tracking-wider">HIT!</p>
                        </>
                      ) : (
                        <>
                          <p className="text-3xl font-black text-white">{(mRatio * 100).toFixed(0)}%</p>
                          <p className="text-[9px] text-white/40 font-bold uppercase tracking-wider">MTD</p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right column: stats + sales input */}
                  <div className="flex-1 min-w-0 space-y-3">
                    {/* Monthly headline */}
                    <div>
                      <p className="text-white leading-none">
                        <span className="text-xl font-black">QAR {formatCurrency(mTotal)}</span>
                        <span className="text-white/30 text-sm ml-1.5">/ {formatCurrency(mTarget)}</span>
                      </p>
                    </div>

                    {/* ── Pace Tracker: Daily Goal vs Actual Need ── */}
                    {(() => {
                      const paceRatio = dailyTarget > 0 ? dailyRequired / dailyTarget : 0;
                      const isBehind = dailyRequired > dailyTarget;
                      const isAhead = dailyRequired < dailyTarget && dailyRequired > 0;
                      const paceColor = isCelebration ? 'text-amber-300' : isBehind ? 'text-red-400' : 'text-emerald-400';
                      const paceBg = isCelebration ? 'bg-amber-500/10 border-amber-500/20' : isBehind ? 'bg-red-500/10 border-red-500/15' : 'bg-emerald-500/10 border-emerald-500/15';
                      const paceLabel = isCelebration ? '🏆 Target Reached!' : isBehind ? '⚠ Behind Pace' : isAhead ? '✓ On Track' : '—';
                      return (
                        <div className={cn('rounded-lg px-3 py-2.5 border', paceBg)}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={cn('text-[10px] font-bold uppercase tracking-wider', paceColor)}>{paceLabel}</span>
                            {!isCelebration && dailyTarget > 0 && (
                              <span className="text-[10px] text-white/25">
                                {isBehind ? `${((paceRatio - 1) * 100).toFixed(0)}% more effort` : `${((1 - paceRatio) * 100).toFixed(0)}% easier`}
                              </span>
                            )}
                          </div>
                          <div className="flex items-end gap-4">
                            <div>
                              <p className="text-[10px] text-white/30 mb-0.5">Daily Goal</p>
                              <p className="text-sm font-black text-white/60">{formatCurrency(dailyTarget)}</p>
                            </div>
                            <div className="text-white/20 text-lg font-light pb-0.5">→</div>
                            <div>
                              <p className="text-[10px] text-white/30 mb-0.5">You Need / Day</p>
                              <p className={cn('text-lg font-black', paceColor)}>{formatCurrency(dailyRequired)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* 3 compact stat pills */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-white/[0.06] rounded-lg px-3 py-2 border border-white/[0.06]">
                        <div className="flex items-center gap-1 mb-0.5">
                          <DollarSign size={10} className="text-emerald-400" />
                          <span className="text-[9px] text-white/30">Today</span>
                        </div>
                        <p className="text-sm font-black text-white">{formatCurrency(loc.current_sales ?? 0)}</p>
                      </div>
                      <div className="bg-white/[0.06] rounded-lg px-3 py-2 border border-white/[0.06]">
                        <div className="flex items-center gap-1 mb-0.5">
                          <Calendar size={10} className="text-amber-400" />
                          <span className="text-[9px] text-white/30">Remaining</span>
                        </div>
                        <p className="text-sm font-black text-white">{formatCurrency(mRemaining)}</p>
                      </div>
                      <div className="bg-white/[0.06] rounded-lg px-3 py-2 border border-white/[0.06]">
                        <div className="flex items-center gap-1 mb-0.5">
                          <TrendingUp size={10} className="text-violet-400" />
                          <span className="text-[9px] text-white/30">Run Rate</span>
                        </div>
                        <p className="text-sm font-black text-white">
                          {formatCurrency(runRate)}
                          {mTarget > 0 && (
                            <span className={cn('text-[9px] ml-0.5', runRateGap >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                              {runRateGap >= 0 ? '+' : ''}{formatCurrency(runRateGap)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Motivator strip */}
                    <div className={cn(
                      'rounded-lg px-3 py-2 border flex items-center gap-2',
                      isCelebration ? 'bg-emerald-500/10 border-emerald-500/20'
                        : (loc.current_sales ?? 0) > 0 ? 'bg-emerald-500/10 border-emerald-500/20'
                        : 'bg-indigo-500/10 border-indigo-500/20'
                    )}>
                      {isCelebration ? (
                        <>
                          <Trophy size={14} className="text-amber-400 shrink-0" />
                          <p className="text-xs text-emerald-300 font-bold">Every additional sale is pure profit!</p>
                        </>
                      ) : (loc.current_sales ?? 0) > 0 ? (
                        <>
                          <CheckCircle size={14} className="text-emerald-400 shrink-0" />
                          <p className="text-xs text-emerald-300/80 font-medium">Today done!</p>
                          <p className="text-[11px] text-white/30 ml-auto">Tomorrow: <span className="font-black text-amber-300/80">QAR {formatCurrency(daysLeft > 1 ? Math.round(mRemaining / (daysLeft - 1)) : mRemaining)}</span></p>
                        </>
                      ) : (
                        <>
                          <Zap size={14} className="text-indigo-400 shrink-0" />
                          <p className="text-xs text-white/50">Sell today to stay on track</p>
                          <p className="text-sm font-black text-indigo-300 ml-auto">QAR {formatCurrency(dailyRequired)}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* ─── Date selector + Sales entry ─── */}
                <div className="mt-4 flex items-center gap-2 mb-2">
                  <button onClick={() => setSelectedDate('')}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-all',
                      !selectedDate
                        ? 'bg-white/15 text-white border-white/20'
                        : 'bg-white/[0.05] text-white/40 border-white/[0.06] hover:bg-white/10'
                    )}>
                    Today
                  </button>
                  <div className="relative">
                    <input
                      type="date"
                      value={selectedDate}
                      min={monthStart}
                      max={todayStr}
                      onChange={e => setSelectedDate(e.target.value)}
                      className="px-3 py-1.5 bg-white/[0.07] border border-white/10 rounded-lg text-[11px] font-bold text-white/70 outline-none focus:border-indigo-400/50 transition-all [color-scheme:dark]"
                    />
                  </div>
                  {selectedDate && selectedDate !== todayStr && (
                    <span className="text-[10px] text-amber-300 font-bold flex items-center gap-1">
                      <AlertTriangle size={11} /> Backfill mode
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
                    <input
                      type="number" placeholder={selectedDate && selectedDate !== todayStr ? `Sales for ${selectedDate}…` : "Enter today's sales…"} step="0.01"
                      value={salesInputs[activeLoc.id] || ''}
                      onChange={e => setSalesInputs(prev => ({ ...prev, [activeLoc.id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') handleSalesSubmit(activeLoc.id); }}
                      className="w-full pl-10 pr-4 py-3.5 bg-white/[0.07] border border-white/10 rounded-xl text-white text-base font-bold placeholder:text-white/20 focus:border-emerald-400/50 focus:ring-2 focus:ring-emerald-400/20 outline-none transition-all"
                    />
                  </div>
                  <motion.button onClick={() => handleSalesSubmit(activeLoc.id)}
                    disabled={savingSales === activeLoc.id}
                    className={cn(
                      'px-6 py-3.5 text-white rounded-xl text-sm font-black shadow-lg transition-all disabled:opacity-50 whitespace-nowrap',
                      isCelebration
                        ? 'bg-gradient-to-r from-amber-500 to-emerald-500 hover:from-amber-600 hover:to-emerald-600'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                    )}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                    {savingSales === activeLoc.id ? <Loader2 size={16} className="animate-spin" /> : 'Submit'}
                  </motion.button>
                </div>

                {/* ─── Daily progress bar — slim at bottom ─── */}
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-white/30 font-medium">Daily progress</span>
                    <span className="font-bold" style={{ color: todayColor }}>
                      {dailyTarget > 0 ? `${(todayRatio * 100).toFixed(0)}%` : '—'}
                    </span>
                  </div>
                  <div className="h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                    <motion.div className="h-full rounded-full"
                      style={{ backgroundColor: todayColor }}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(todayRatio * 100, 100)}%` }}
                      transition={{ duration: 0.8, ease: 'easeOut' }} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        ) : (
          /* ─── RECENT ENTRIES TAB ─── */
          <motion.div key="history-tab"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}>

            {recentEntries.length > 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-3 bg-slate-50 border-b border-slate-100 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <span>Branch</span>
                  <span>Date</span>
                  <span className="text-right">Amount</span>
                </div>
                <div className="divide-y divide-slate-50 max-h-[500px] overflow-y-auto">
                  {recentEntries.map((entry: any, i: number) => {
                    const createdStr = entry.created_at ? String(entry.created_at).slice(0, 10) : '';
                    const isBackfill = createdStr && entry.entry_date && createdStr !== entry.entry_date;
                    return (
                      <motion.div key={entry.id || i}
                        initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-3 items-center hover:bg-slate-50/60 transition-colors">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center shrink-0', isBackfill ? 'bg-amber-50' : 'bg-emerald-50')}>
                            {isBackfill ? <Clock size={12} className="text-amber-500" /> : <DollarSign size={12} className="text-emerald-600" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-800 truncate">{entry.location_name || 'Unknown'}</p>
                            {isBackfill && <span className="text-[9px] text-amber-500 font-bold">Backfill</span>}
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-400 font-medium whitespace-nowrap">{entry.entry_date}</p>
                        <p className="text-sm font-black text-emerald-600 text-right">QAR {formatCurrency(Number(entry.sales_amount ?? 0))}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
                <Clock size={28} className="mx-auto text-slate-200 mb-3" />
                <p className="text-sm font-semibold text-slate-400">No entries this month yet.</p>
                <p className="text-xs text-slate-300 mt-1">Submit sales from the Dashboard tab to see them here.</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
